import { Form, Input } from 'antd/lib';
import { Option, OptionDetails } from 'api/api_code';
import { OrganizationsEndpoints } from 'api/axios/axiosAPIGroups';
import { Icon } from 'components/UI/Icon/Icon';
import { IconNames } from 'components/UI/Icon/icons';
import { useHTTPRequestHandler } from 'hooks/useHTTPRequestHandler';
import useTranslation from 'next-translate/useTranslation';
import { ChangeEvent, useEffect, useState } from 'react';
import { usePairingFormHandler } from '../hooks/usePairingFormHandler';
import styles from './CameraPairingForm.module.css';
import CameraPairingFormFooter from './CameraPairingFormFooter';
import { Dropdown, Tooltip } from '@atvlibs/atvdesignsystem';
import transformOptions from 'helpers/transformOptions';

const CameraPairingForm = () => {
	const { t } = useTranslation('cameras');
	const { t: tCommon } = useTranslation('common');
	const [organizations, setOrganizations] = useState<Option[]>([]);
	const { requestHandler } = useHTTPRequestHandler();
	const {
		form,
		cameraType,
		pairingData,
		serialStatus,
		inputHandler,
		setPairingData,
		setSerialStatus,
	} = usePairingFormHandler();

	useEffect(() => {
		requestHandler({
			requestPromise: OrganizationsEndpoints.getOrganizationsOrganizationsGet(),
			onOkCallback: (userOrgs: Record<string, OptionDetails>) => {
				setOrganizations(userOrgs.organizations.opts);
				if (userOrgs.organizations.opts.length === 1) {
					setPairingData((prevPairing) => ({
						...prevPairing,
						organization_uuid: userOrgs.organizations.opts[0].value,
					}));
				}
			},
		});
	}, []);

	useEffect(() => {
		form.resetFields();
	}, [organizations]);

	useEffect(() => {
		if (!serialStatus) return;
		form.setFields(serialStatus);
	}, [serialStatus]);

	return (
		<Form
			form={form}
			layout="vertical"
			requiredMark={false}
			className={cameraType === 'ONE' ? styles.formGrid : styles.form}
			initialValues={{
				name: pairingData.name,
				serial: pairingData.serial_number_producer,
				organization:
					organizations.length === 1
						? organizations[0].display_name
						: pairingData.organization_uuid,
			}}
		>
			<Form.Item
				label={t('pairNewCameraModal.cameraName.label')}
				name="camera-pairing-name"
				className={
					cameraType === 'ONE' ? styles.formItemNoMargin : styles.formItem
				}
				rules={[
					{
						required: true,
						message: tCommon('errors.inputMessage', {
							fieldName: t('pairNewCameraModal.cameraName.label'),
						}),
					},
					{
						max: 50,
						message: tCommon('errors.lengthMessage', {
							fieldName: t('pairNewCameraModal.cameraName.label'),
						}),
					},
				]}
			>
				<Input
					id={'camera-pairing-name'}
					placeholder={t('pairNewCameraModal.cameraName.placeholder')}
					onChange={(e: ChangeEvent<HTMLInputElement>) =>
						inputHandler({ dataKey: 'name', value: e.target.value })
					}
					maxLength={51}
				/>
			</Form.Item>
			<Form.Item
				label={
					<span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
						{t('pairNewCameraModal.serialNumber.label')}
						<Tooltip
							title={t('pairNewCameraModal.serialNumber.tooltip')}
							customBodyStyle={{ textAlign: 'center' }}
						>
							<Icon component={IconNames.INFO} size={14} />
						</Tooltip>
					</span>
				}
				name="serial"
				className={
					cameraType === 'ONE' ? styles.formItemNoMargin : styles.formItem
				}
				rules={[
					{
						pattern: new RegExp(/[A-Z0-9]+/g),
						len: 20,
						max: 20,
						message: t('pairNewCameraModal.serialNumber.ruleError'),
					},
				]}
			>
				<Input
					placeholder={t('pairNewCameraModal.serialNumber.placeholder')}
					onChange={(e: ChangeEvent<HTMLInputElement>) => {
						setSerialStatus([]);
						inputHandler({
							dataKey: 'serial_number_producer',
							value: e.target.value.toUpperCase(),
						});
					}}
					maxLength={20}
					className={styles.inputIcon}
				/>
			</Form.Item>
			<Form.Item
				label={t('pairNewCameraModal.organization.label')}
				name="organization"
				className={
					cameraType === 'ONE' ? styles.formItemNoMargin : styles.formItem
				}
			>
				{organizations.length === 1 ? (
					<Input
						placeholder={organizations[0].display_name}
						value={organizations[0].display_name}
						disabled={true}
						suffix={<Icon component={IconNames.LOCK} size={24} />}
						className={styles.inputIcon}
					/>
				) : (
					<Dropdown
						placeholder={t('pairNewCameraModal.organization.placeholder')}
						options={transformOptions(organizations)}
						onChange={(value) =>
							inputHandler({
								dataKey: 'organization_uuid',
								value: value as string,
							})
						}
					/>
				)}
			</Form.Item>
			{cameraType === 'ONE' && (
				<Form.Item
					label={
						<span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							{t('pairNewCameraModal.serialNumber.label')}
							<Tooltip
								title={t('pairNewCameraModal.serialNumber.tooltip')}
								customBodyStyle={{ textAlign: 'center' }}
							>
								<Icon component={IconNames.INFO} size={14} />
							</Tooltip>
						</span>
					}
					className={
						cameraType === 'ONE' ? styles.formItemNoMargin : styles.formItem
					}
				>
					<Input
						placeholder={t('pairNewCameraModal.serialNumber.placeholder')}
						onChange={(e: ChangeEvent<HTMLInputElement>) =>
							inputHandler({
								dataKey: 'serial_number_producer',
								value: String(e.target.value) || '',
							})
						}
					/>
				</Form.Item>
			)}
		</Form>
	);
};

CameraPairingForm.Footer = CameraPairingFormFooter;

export { CameraPairingForm };
